import React, { createContext, useContext, useEffect, useState } from 'react';
import { BrandingConfig, loadBrandingConfig, getDefaultBranding, applyBrandingColors } from '../config/branding';

interface BrandingContextType {
  branding: BrandingConfig;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  reloadBranding: () => Promise<void>;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<BrandingConfig>(getDefaultBranding());
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    loadBrandingConfig().then(config => {
      setBranding(config);
      applyBrandingColors(config, isDarkMode);
      document.title = config.appName;
    });
  }, []);

  useEffect(() => {
    applyBrandingColors(branding, isDarkMode);
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode, branding]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const reloadBranding = async () => {
    const config = await loadBrandingConfig();
    setBranding(config);
    applyBrandingColors(config, isDarkMode);
  };

  return (
    <BrandingContext.Provider value={{ branding, isDarkMode, toggleDarkMode, reloadBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}